import { NetSuiteCustomer, SchoolLookUpSiteData } from 'types'

export type NormalizedSiteData = Omit<
  SchoolLookUpSiteData,
  'netsuiteCustomerId'
> & {
  netsuiteCustomerId: number | null
}

export const NOT_LISTED_CUSTOMER_ID = 0
export const EMPTY_SELECT_VALUE = 'blank'
export const NOT_LISTED_SELECT_VALUE = '0'

export const EMPTY_NETSUITE_CUSTOMER_SELECTION = {
  id: null,
  nsCustomerId: '',
  companyName: '',
} as const

export const NOT_LISTED_NETSUITE_CUSTOMER_SELECTION = {
  id: NOT_LISTED_CUSTOMER_ID,
  nsCustomerId: '',
  companyName: '',
} as const

export const EMPTY_SITE_DATA: NormalizedSiteData = {
  city: '',
  companyName: '',
  country: '',
  netsuiteCustomerId: null,
  nsCustomerId: '',
  organizationName: '',
  region: '',
}

const CLEARED_SCHOOL_SELECTION: Partial<NormalizedSiteData> = {
  companyName: '',
  netsuiteCustomerId: null,
  nsCustomerId: '',
  organizationName: '',
}

export const isListedCustomer = (customer: NetSuiteCustomer): boolean =>
  customer.id !== NOT_LISTED_CUSTOMER_ID

export const findCustomerInData = (
  customers: NetSuiteCustomer[] | null,
  siteData: Pick<NormalizedSiteData, 'netsuiteCustomerId' | 'nsCustomerId'>,
): NetSuiteCustomer | undefined => {
  if (!customers) {
    return undefined
  }

  const { netsuiteCustomerId, nsCustomerId } = siteData

  if (netsuiteCustomerId && netsuiteCustomerId !== NOT_LISTED_CUSTOMER_ID) {
    return customers.find(
      customer =>
        customer.id === netsuiteCustomerId && isListedCustomer(customer),
    )
  }

  if (nsCustomerId) {
    return customers.find(
      customer =>
        customer.nsCustomerId === nsCustomerId && isListedCustomer(customer),
    )
  }

  return undefined
}

export const isCustomerInLoadedData = (
  customers: NetSuiteCustomer[] | null,
  siteData: Pick<NormalizedSiteData, 'netsuiteCustomerId' | 'nsCustomerId'>,
): boolean => findCustomerInData(customers, siteData) !== undefined

export const resolveNetsuiteCustomerId = (
  siteData: SchoolLookUpSiteData,
): number | null => {
  if (
    siteData.netsuiteCustomerId !== null &&
    siteData.netsuiteCustomerId !== undefined
  ) {
    return siteData.netsuiteCustomerId
  }

  if (siteData.organizationName) {
    return NOT_LISTED_CUSTOMER_ID
  }

  return null
}

export const normalizeSiteData = (
  siteData?: SchoolLookUpSiteData | null,
): NormalizedSiteData => {
  if (!siteData) {
    return EMPTY_SITE_DATA
  }

  return {
    city: siteData.city || '',
    companyName: siteData.companyName || '',
    country: siteData.country || '',
    netsuiteCustomerId: resolveNetsuiteCustomerId(siteData),
    nsCustomerId: siteData.nsCustomerId || '',
    organizationName: siteData.organizationName || '',
    region: siteData.region || '',
  }
}

export const getDownstreamClearForField = (
  field: string,
  current: NormalizedSiteData,
): Partial<NormalizedSiteData> => {
  if (field === 'city') {
    return {
      ...CLEARED_SCHOOL_SELECTION,
      country: current.country,
      region: current.region,
    }
  }

  if (field === 'region') {
    return {
      ...CLEARED_SCHOOL_SELECTION,
      city: '',
      country: current.country,
    }
  }

  if (field === 'country') {
    return {
      ...CLEARED_SCHOOL_SELECTION,
      city: '',
      region: '',
    }
  }

  return {}
}
